import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_slice_generation, use_slice_ui } from '@/hooks/store/use_slices';
import { HOOK__drop_zone } from '@/services/drag_and_drop/HOOK__drop_zone';
import { LOCAL__ui_set_boolean } from '@/state/ui/_base/LOCAL__ui_set_boolean';
import { LOCAL__ui_drag_and_drop_set_image_dragged } from '@/state/ui/drag_and_drop/LOCAL__ui_drag_and_drop_set_image_dragged';
import { UTILS__graph_data } from '@/utils/UTILS__graph_data';
import { assert_false } from '@shared/asserts/utils_asserts';
import { FIELD__image_asset } from '@shared/dto/generation/graph_invoke/_fields/image/FIELD__image_asset';
import { useMemo, useRef } from 'react';
import { HOOK__get_div_size } from '../../../../../../../_hooks/HOOK__get_div_size';
import { G_DRAG_AND_DROP__area_with_label } from './G_DRAG_AND_DROP__area_with_label';

interface PROPS {
  field: FIELD__image_asset;
}

export function G_GRAPH__DRAG_AND_DROP__image_internal({ field }: PROPS) {
  /*
   * STORE
   */

  const dispatch = use_dispatch();

  const { drag_and_drop__image_internal_zones_show, drag_and_drop__image_internal_dragged, g_general__ui_disabled } = use_slice_ui([
    'drag_and_drop__image_internal_zones_show',
    'drag_and_drop__image_internal_dragged',
    'g_general__ui_disabled',
  ]);

  const { generation_current } = use_slice_generation(['generation_current']);
  let { graph } = generation_current.tool;

  /*
   * STATE
   */

  const is_image_with_similar_content_dragged = useMemo(() => get_is_image_with_similar_content_dragged(), [field, drag_and_drop__image_internal_dragged]);
  const { ref_element__set, div_element } = HOOK__get_div_size();

  const ref__is_loading = useRef(false);

  HOOK__drop_zone(field.meta.field_id, div_element, () => onDrop());

  /*
   * EFFECTS
   */

  /*
   * EVENTS
   */

  async function onDrop() {
    if (drag_and_drop__image_internal_dragged.is_stub()) return;

    if (!is_interactive()) return;

    assert_false(drag_and_drop__image_internal_dragged.is_stub());

    /*
     *
     */
    let field_new = field.clone();
    field_new.image__asset_use(drag_and_drop__image_internal_dragged.clone());

    /*
     *
     */
    dispatch(LOCAL__ui_set_boolean({ key: 'drag_and_drop__image_internal_zones_show', value: false }));
    dispatch(LOCAL__ui_drag_and_drop_set_image_dragged());

    /*
     *
     */
    ref__is_loading.current = true;

    await UTILS__graph_data.update_field(graph, field_new);

    ref__is_loading.current = false;
  }

  function onDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
  }

  /*
   * METHODS
   */

  function get_is_image_with_similar_content_dragged() {
    let out = drag_and_drop__image_internal_dragged.hash_sha256_content == field.hash_sha256_content;

    return out;
  }

  /*
   * RENDERS
   */

  function get_class_name_opacity() {
    return drag_and_drop__image_internal_zones_show ? ' opacity-100 ' : ' opacity-0 ';
  }

  function is_interactive() {
    if (drag_and_drop__image_internal_dragged.is_stub()) return false;
    if (ref__is_loading.current) return false;

    let out = true;

    if (!drag_and_drop__image_internal_zones_show) {
      out = false;
    } else if (!field.is_ready_for_manual_edit()) {
      out = false;
    } else if (field.category != drag_and_drop__image_internal_dragged.category) {
      out = false;
    } else if (is_image_with_similar_content_dragged) {
      out = false;
    }

    return out;
  }

  function get_text() {
    let out = '';

    if (drag_and_drop__image_internal_dragged.is_stub()) {
      out = 'No image to drop';
    } else if (!field.is_ready_for_manual_edit()) {
      out = 'Read Only Field';
    } else if (field.category != drag_and_drop__image_internal_dragged.category) {
      out = 'Not this image type';
    } else if (is_image_with_similar_content_dragged) {
      out = 'Same Image';
    } else {
      out = 'Drop Image Here';
    }

    return out;
  }

  /*
   * HTML
   */

  if (g_general__ui_disabled) return null;

  // todo: use animation border instead of static
  //todo: animate on hover
  return (
    <div
      {...{ onDragOver }}
      ref={ref_element__set}
      data-drag-and-drop-zone-graph-field={field.meta.field_id}
      className={`
        info_drag_and_drop_image_internal 
    absolute inset-0 z-50
    flex-center p-4
    text-center  text-white 
    bg-black
    ${get_class_name_opacity()}
    ${is_interactive() ? ' border border-2 border-dashed border-component-item-border pointer-events-auto ' : ' pointer-events-none '}
    rounded-lg
  `}
    >
      {/* content */}
      <G_DRAG_AND_DROP__area_with_label text={get_text()} class_name="bg-white/10" />
    </div>
  );
}
